import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Search, 
  MapPin, 
  Clock, 
  Users, 
  Briefcase, 
  Star, 
  Mail,
  Phone,
  CheckCircle,
  TrendingUp,
  Award,
  Calendar
} from 'lucide-react';
import JobDetailsModal from '../JobDetailsModal';
import { useNavigate } from 'react-router-dom';

const Jobs = () => {
  const navigate =useNavigate()
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedLocation, setSelectedLocation] = useState('All');
  const [selectedType, setSelectedType] = useState('All');
  const [selectedJob, setSelectedJob] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const jobCategories = ['All', 'Consulting', 'HR', 'Research', 'Technical', 'Management', 'Operations'];
  const locations = ['All', 'Patna', 'Delhi', 'Mumbai', 'Bangalore', 'Remote'];
  const jobTypes = ['All', 'Full-time', 'Part-time', 'Contract', 'Internship'];

  const handleViewDetails = (job) => {
    setSelectedJob(job);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedJob(null);
  };

  const jobs = [
    {
      id: 1,
      title: 'Senior NGO Consultant',
      company: 'Lumbini Consultancy',
      location: 'Patna',
      type: 'Full-time',
      category: 'Consulting',
      experience: '5-8 years',
      salary: '₹8-12 LPA',
      postedDate: '2 days ago',
      description: 'Lead NGO consulting projects, develop strategies for social impact organizations, and manage client relationships.',
      requirements: [
        'Master\'s degree in Social Work or related field',
        '5+ years experience in NGO sector',
        'Strong analytical and communication skills',
        'Experience with project management'
      ],
      benefits: [
        'Health insurance',
        'Flexible working hours',
        'Professional development opportunities',
        'Performance bonuses'
      ],
      isUrgent: true,
      isFeatured: true
    },
    {
      id: 2,
      title: 'HR Business Partner',
      company: 'Lumbini Consultancy',
      location: 'Delhi',
      type: 'Full-time',
      category: 'HR',
      experience: '3-5 years',
      salary: '₹6-9 LPA',
      postedDate: '1 week ago',
      description: 'Partner with business units to provide HR support, manage talent acquisition, and drive employee engagement initiatives.',
      requirements: [
        'MBA in HR or related field',
        '3+ years HR experience',
        'Knowledge of labor laws',
        'Strong interpersonal skills'
      ],
      benefits: [
        'Competitive salary',
        'Work from home options',
        'Learning and development budget',
        'Team building activities'
      ],
      isUrgent: false,
      isFeatured: false
    },
    {
      id: 3,
      title: 'Research Analyst',
      company: 'Lumbini Consultancy',
      location: 'Remote',
      type: 'Full-time',
      category: 'Research',
      experience: '2-4 years',
      salary: '₹4-7 LPA',
      postedDate: '3 days ago',
      description: 'Conduct market research, analyze data, and prepare reports for various consulting projects.',
      requirements: [
        'Bachelor\'s degree in Economics/Statistics',
        '2+ years research experience',
        'Proficiency in Excel and SPSS',
        'Strong analytical skills'
      ],
      benefits: [
        'Remote work flexibility',
        'Research publication opportunities',
        'Conference attendance support',
        'Mentorship program'
      ],
      isUrgent: false,
      isFeatured: true
    },
    {
      id: 4,
      title: 'Technical Consultant',
      company: 'Lumbini Consultancy',
      location: 'Mumbai',
      type: 'Contract',
      category: 'Technical',
      experience: '4-6 years',
      salary: '₹10-15 LPA',
      postedDate: '5 days ago',
      description: 'Provide technical expertise for digital transformation projects and technology implementation.',
      requirements: [
        'Engineering degree in IT/Computer Science',
        '4+ years technical consulting experience',
        'Knowledge of cloud technologies',
        'Project management certification preferred'
      ],
      benefits: [
        'High project-based compensation',
        'Latest technology exposure',
        'Flexible project timelines',
        'Technical training programs'
      ],
      isUrgent: true,
      isFeatured: false
    },
    {
      id: 5,
      title: 'Operations Manager',
      company: 'Lumbini Consultancy',
      location: 'Patna',
      type: 'Full-time',
      category: 'Management',
      experience: '6-10 years',
      salary: '₹12-18 LPA',
      postedDate: '1 week ago',
      description: 'Oversee daily operations, manage teams, and ensure efficient delivery of consulting services.',
      requirements: [
        'MBA in Operations Management',
        '6+ years operations experience',
        'Team leadership experience',
        'Process improvement expertise'
      ],
      benefits: [
        'Leadership role',
        'Performance incentives',
        'Executive development program',
        'Comprehensive benefits package'
      ],
      isUrgent: false,
      isFeatured: true
    },
    {
      id: 6,
      title: 'Business Development Intern',
      company: 'Lumbini Consultancy',
      location: 'Bangalore',
      type: 'Internship',
      category: 'Operations',
      experience: '0-1 years',
      salary: '₹15,000-25,000/month',
      postedDate: '2 days ago',
      description: 'Support business development activities, research potential clients, and assist in proposal preparation.',
      requirements: [
        'Pursuing or completed MBA',
        'Strong communication skills',
        'Basic knowledge of business processes',
        'Eagerness to learn'
      ],
      benefits: [
        'Hands-on experience',
        'Mentorship from senior team',
        'Potential full-time opportunity',
        'Stipend and certificates'
      ],
      isUrgent: false,
      isFeatured: false
    }
  ];

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || job.category === selectedCategory;
    const matchesLocation = selectedLocation === 'All' || job.location === selectedLocation;
    const matchesType = selectedType === 'All' || job.type === selectedType;
    
    return matchesSearch && matchesCategory && matchesLocation && matchesType;
  });

  const featuredJobs = jobs.filter(job => job.isFeatured);

  return (
    <div className="min-h-screen bg-white">
      <JobDetailsModal 
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        job={selectedJob}
      />
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-primary-50 via-white to-accent-50 text-gray-900 pt-32 pb-20 overflow-hidden">
        {/* Geometric Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-32 h-32 border-2 border-primary-300 rounded-full"></div>
          <div className="absolute top-40 right-20 w-24 h-24 border-2 border-accent-300 transform rotate-45"></div>
          <div className="absolute bottom-32 left-1/4 w-20 h-20 border-2 border-primary-400 rounded-full"></div>
          <div className="absolute bottom-20 right-1/3 w-16 h-16 border-2 border-accent-400 transform rotate-12"></div>
        </div>
        
        {/* Floating Elements */}
        <div className="absolute top-16 left-1/4 w-4 h-4 bg-primary-400 rounded-full animate-bounce"></div>
        <div className="absolute top-32 right-1/4 w-3 h-3 bg-accent-400 rounded-full animate-bounce delay-1000"></div>
        <div className="absolute bottom-40 left-1/3 w-2 h-2 bg-primary-500 rounded-full animate-bounce delay-2000"></div>
        
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
          <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div className="space-y-6">
                <div className="inline-flex items-center px-4 py-2 bg-primary-100 text-primary-700 rounded-full text-sm font-semibold">
                  <Briefcase className="h-4 w-4 mr-2" />
                  We're Hiring!
                </div>
                <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                  Join Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-accent-600">Dream Team</span>
                </h1>
                <p className="text-xl text-gray-700 leading-relaxed">
                  Be part of a dynamic team that's revolutionizing consulting and creating meaningful impact across industries. Your career journey starts here.
                </p>
              </div>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <motion.a
                  href="https://forms.gle/v4StRzRMGARiSKcR6"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="inline-block bg-gradient-to-r from-primary-600 to-accent-600 text-white px-8 py-4 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  Apply for Jobs
                </motion.a>
                <motion.button
                onClick={()=>navigate('/contactus')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="border-2 border-primary-600 text-primary-600 px-8 py-4 rounded-xl font-semibold hover:bg-primary-600 hover:text-white transition-all duration-300"
                >
                  Learn About Us
                </motion.button>
              </div>
            </motion.div>
            
            {/* Right Content - Stats Cards */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="grid grid-cols-2 gap-6"
            >
              {[
                { icon: Briefcase, value: "6", label: "Open Positions", color: "from-blue-500 to-blue-600" },
                { icon: Users, value: "25+", label: "Team Members", color: "from-green-500 to-green-600" },
                { icon: Award, value: "8+", label: "Years Experience", color: "from-purple-500 to-purple-600" },
                { icon: TrendingUp, value: "98%", label: "Success Rate", color: "from-orange-500 to-orange-600" }
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                  className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-100"
                >
                  <div className={`w-12 h-12 bg-gradient-to-r ${stat.color} rounded-xl flex items-center justify-center mb-4`}>
                    <stat.icon className="h-6 w-6 text-white" />
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
                  <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>
            </div>
        </div>
      </div>

      <div className="container-custom py-16">
        {/* Search and Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-gradient-to-r from-gray-50 to-white rounded-2xl shadow-xl p-8 mb-12 border border-gray-200"
        >
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Search Bar */}
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search jobs by title or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white shadow-sm"
              />
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white shadow-sm"
              >
                {jobCategories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>

              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                className="px-4 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white shadow-sm"
              >
                {locations.map(location => (
                  <option key={location} value={location}>{location}</option>
                ))}
              </select>

              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="px-4 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white shadow-sm"
              >
                {jobTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          </div>
        </motion.div>

        {/* Featured Jobs */}
        {featuredJobs.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-12"
          >
            <h2 className="text-3xl font-bold mb-8 text-center">Featured Opportunities</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredJobs.map((job, index) => (
                <motion.div
                  key={job.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-gradient-to-r from-white to-gray-50 rounded-2xl shadow-lg p-6 border-l-4 border-primary-500 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-1">{job.title}</h3>
                      <p className="text-primary-600 font-semibold">{job.company}</p>
                    </div>
                    <div className="flex flex-col items-end space-y-1">
                      {job.isUrgent && (
                        <span className="bg-red-100 text-red-600 px-2 py-1 rounded-full text-xs font-semibold">
                          Urgent
                        </span>
                      )}
                      <span className="bg-primary-100 text-primary-600 px-2 py-1 rounded-full text-xs font-semibold">
                        Featured
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-gray-600">
                      <MapPin className="h-4 w-4 mr-2" />
                      <span className="text-sm">{job.location}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Clock className="h-4 w-4 mr-2" />
                      <span className="text-sm">{job.type}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Calendar className="h-4 w-4 mr-2" />
                      <span className="text-sm">{job.postedDate}</span>
                    </div>
                  </div>

                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">{job.description}</p>

                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-primary-600">{job.salary}</span>
                    <motion.a
                      href="https://forms.gle/v4StRzRMGARiSKcR6"
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="inline-block bg-gradient-to-r from-primary-600 to-accent-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:from-primary-700 hover:to-accent-700 transition-all duration-300 shadow-md hover:shadow-lg"
                    >
                      Apply Now
                    </motion.a>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* All Jobs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold">All Job Openings</h2>
            <span className="text-gray-600">{filteredJobs.length} positions found</span>
          </div>

          {filteredJobs.length === 0 ? (
            <div className="text-center py-12">
              <Briefcase className="h-16 w-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No jobs found</h3>
              <p className="text-gray-500">Try adjusting your search criteria or check back later for new opportunities.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {filteredJobs.map((job, index) => (
                <motion.div
                  key={job.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-gradient-to-r from-white to-gray-50 rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100"
                >
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900 mb-2">{job.title}</h3>
                          <p className="text-primary-600 font-semibold text-lg">{job.company}</p>
                        </div>
                        <div className="flex flex-col items-end space-y-2">
                          {job.isUrgent && (
                            <span className="bg-red-100 text-red-600 px-3 py-1 rounded-full text-sm font-semibold">
                              Urgent
                            </span>
                          )}
                          {job.isFeatured && (
                            <span className="bg-primary-100 text-primary-600 px-3 py-1 rounded-full text-sm font-semibold">
                              Featured
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div className="flex items-center text-gray-600">
                          <MapPin className="h-5 w-5 mr-2" />
                          <span>{job.location}</span>
                        </div>
                        <div className="flex items-center text-gray-600">
                          <Clock className="h-5 w-5 mr-2" />
                          <span>{job.type}</span>
                        </div>
                        <div className="flex items-center text-gray-600">
                          <Users className="h-5 w-5 mr-2" />
                          <span>{job.experience}</span>
                        </div>
                        <div className="flex items-center text-gray-600">
                          <Calendar className="h-5 w-5 mr-2" />
                          <span>{job.postedDate}</span>
                        </div>
                      </div>

                      <p className="text-gray-600 mb-6">{job.description}</p>

                      <div className="grid md:grid-cols-2 gap-6 mb-6">
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-3">Key Requirements:</h4>
                          <ul className="space-y-2">
                            {job.requirements.slice(0, 3).map((req, idx) => (
                              <li key={idx} className="flex items-start text-sm text-gray-600">
                                <CheckCircle className="h-4 w-4 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                                <span>{req}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-3">Benefits:</h4>
                          <ul className="space-y-2">
                            {job.benefits.slice(0, 3).map((benefit, idx) => (
                              <li key={idx} className="flex items-start text-sm text-gray-600">
                                <Star className="h-4 w-4 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                                <span>{benefit}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="lg:ml-8 lg:text-right">
                      <div className="text-2xl font-bold text-primary-600 mb-4">{job.salary}</div>
                      <div className="space-y-3">
                        <motion.a
                          href="https://forms.gle/v4StRzRMGARiSKcR6"
                          target="_blank"
                          rel="noopener noreferrer"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="block w-full bg-gradient-to-r from-primary-600 to-accent-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-primary-700 hover:to-accent-700 transition-all duration-300 shadow-lg hover:shadow-xl text-center"
                        >
                          Apply Now
                        </motion.a>
                        <motion.button
                          onClick={() => handleViewDetails(job)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="w-full border border-primary-600 text-primary-600 px-6 py-3 rounded-lg font-semibold hover:bg-primary-600 hover:text-white transition-all duration-300"
                        >
                          View Details
                        </motion.button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Application Process */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-16 bg-gradient-to-r from-primary-600 to-accent-600 rounded-2xl p-8 text-white shadow-xl"
        >
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-4">How to Apply</h2>
            <p className="text-lg opacity-90">Follow these simple steps to join our team</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                title: "Find Your Role",
                description: "Browse our open positions and find the role that matches your skills and interests."
              },
              {
                step: "02", 
                title: "Submit Application",
                description: "Click 'Apply Now' and submit your resume along with a cover letter."
              },
              {
                step: "03",
                title: "Interview Process",
                description: "Our team will review your application and contact you for the next steps."
              }
            ].map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold">{step.step}</span>
                </div>
                <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                <p className="opacity-90">{step.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <motion.a
              href="https://forms.gle/v4StRzRMGARiSKcR6"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-block bg-white text-primary-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-colors shadow-lg hover:shadow-xl mb-6"
            >
              Apply for Your Dream Job Now
            </motion.a>
            <p className="mb-4">Have questions about our application process?</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="mailto:hr@lumconser.com"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center justify-center space-x-2 bg-white text-primary-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg hover:shadow-xl"
              >
                <Mail className="h-5 w-5" />
                <span>hr@lumconser.com</span>
              </motion.a>
              <motion.a
                href="tel:+917979060329"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center justify-center space-x-2 bg-white text-primary-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg hover:shadow-xl"
              >
                <Phone className="h-5 w-5" />
                <span>+91-7979060329</span>
              </motion.a>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Jobs;


